/* Native GET catalogue navigation, progressively enhanced with section rendering. */
(() => {
  if (window.rdbCatalogNavigation) return;
  window.rdbCatalogNavigation = true;

  const ROOT = '[data-catalog-root]';
  const FILTER_DIALOG = '[data-catalog-filter-dialog]';
  const filterDialogs = new WeakMap();
  const CONTROL = 'a,button,input,select,summary';
  const SECTION_HASHES = new Set(['#CatalogGrid', '#CatalogGifts', '#CatalogPersonalise']);
  let pending;
  let pendingAutomatic = false;
  let requestNumber = 0;
  let moreObserver;
  let positionTimer;
  let totalRequestNumber = 0;
  let clearSkeletons = () => {};

  const showSkeletons = root => {
    const grid = root.querySelector('[data-catalog-grid]');
    if (!grid) return;
    const cells = [...grid.querySelectorAll('[data-catalog-card]')];
    const measure = card => {
      const bounds = card.getBoundingClientRect();
      const bones = [];
      const add = (rect, kind, inset = 0) => {
        if (!rect.width || !rect.height || !bounds.width || !bounds.height) return;
        bones.push({ kind, left: (rect.left - bounds.left) / bounds.width * 100,
          top: (rect.top - bounds.top + inset) / bounds.height * 100,
          width: rect.width / bounds.width * 100, height: (rect.height - inset * 2) / bounds.height * 100 });
      };
      const boxes = (selector, kind) => card.querySelectorAll(selector).forEach(element => add(element.getBoundingClientRect(), kind));
      const text = selector => card.querySelectorAll(selector).forEach(element => {
        const range = document.createRange();
        range.selectNodeContents(element);
        const lines = [];
        for (const rect of range.getClientRects()) {
          const line = lines.find(row => Math.abs(row.top - rect.top) < 2 && Math.abs(row.height - rect.height) < 2);
          if (line) { line.left = Math.min(line.left, rect.left); line.right = Math.max(line.right, rect.right); line.width = line.right - line.left; }
          else lines.push({ top: rect.top, left: rect.left, right: rect.right, width: rect.width, height: rect.height });
        }
        lines.forEach(rect => add(rect, 'text', Math.min(3, rect.height * .15)));
      });
      boxes('.d2-product-card__media', 'image');
      boxes('.rdb-image-label, .d2-product-tag', 'tag');
      boxes('.rosarium-wishlist-action__icon, .d2-product-card__delivery > svg', 'round');
      text('h3 a, .d2-price > span, .d2-price > strong, .d2-product-card__delivery > span, .rdb-rating__average, .rdb-rating__count, .rc-addon-card__help, .rc-text-result p');
      boxes('.rdb-rating__stars', 'text');
      return { ratio: `${bounds.width} / ${bounds.height}`, bones };
    };
    const shapes = cells.map(measure);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => entry.target.classList.toggle('is-visible', entry.isIntersecting));
    }) : null;
    const pause = () => root.toggleAttribute('data-budget-paused', document.hidden);
    const wasInert = grid.inert;
    const placeholders = [];
    if (!shapes.length) {
      const style = getComputedStyle(grid);
      const width = parseFloat(style.gridTemplateColumns) || grid.clientWidth / 2;
      const height = width + 130;
      const line = (top, length, size = 12) => ({ kind: 'text', left: style.direction === 'rtl' ? 100 - length : 0,
        top: top / height * 100, width: length, height: size / height * 100 });
      const shape = { ratio: `${width} / ${height}`, bones: [
        { kind: 'image', left: 0, top: 0, width: 100, height: width / height * 100 },
        line(width + 14, 80), line(width + 38, 58, 20), line(width + 78, 75), line(width + 104, 55)
      ] };
      shapes.push(...Array(12).fill(shape));
    }
    shapes.forEach((shape, index) => {
      const card = document.createElement('div');
      card.className = 'd2-product-card d2-product-card--skeleton';
      card.setAttribute('aria-hidden', 'true');
      card.inert = true;
      card.style.setProperty('--skeleton-ratio', shape.ratio);
      shape.bones.forEach(bone => {
        const part = document.createElement('span');
        part.className = `d2-skeleton-bone d2-skeleton-bone--${bone.kind}`;
        for (const key of ['left', 'top', 'width', 'height']) part.style[key] = `${bone[key]}%`;
        card.append(part);
      });
      (cells[index] || grid).append(card);
      if (observer) observer.observe(card);
      else card.classList.add('is-visible');
      placeholders.push(card);
    });
    grid.inert = true;
    root.setAttribute('data-catalog-skeleton', '');
    document.addEventListener('visibilitychange', pause);
    pause();
    clearSkeletons = () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', pause);
      placeholders.forEach(card => card.remove());
      grid.inert = wasInert;
      root.removeAttribute('data-catalog-skeleton');
      root.removeAttribute('data-budget-paused');
      clearSkeletons = () => {};
    };
  };

  const urlFor = value => {
    const url = new URL(value, window.location.href);
    const current = new URL(window.location.href);
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview && !url.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', preview);
    url.searchParams.delete('section_id');
    if (/\/collections\/all\/?$/.test(url.pathname) && !url.searchParams.has('sort_by') &&
        ![...url.searchParams.keys()].some(key => key.startsWith('filter.'))) {
      url.searchParams.set('sort_by', 'created-descending');
      url.searchParams.set('rdb_featured', '1');
    }
    // Native pagination retains the sort but omits the custom Featured marker.
    if (url.pathname === current.pathname && /\/collections\/all\/?$/.test(url.pathname) &&
        url.searchParams.has('page') && url.searchParams.get('sort_by') === 'created-descending' &&
        current.searchParams.get('rdb_featured') === '1' && current.searchParams.get('sort_by') === 'created-descending' &&
        ![...url.searchParams.keys(), ...current.searchParams.keys()].some(key => key.startsWith('filter.'))) {
      url.searchParams.set('rdb_featured', '1');
    }
    return url;
  };

  const describeFocus = (control, root) => {
    if (!control || !root.contains(control)) return null;
    return {
      id: control.id,
      tag: control.tagName,
      name: control.getAttribute('name'),
      value: control.value,
      href: control.getAttribute('href'),
      more: control.hasAttribute('data-catalog-more'),
      summary: control.matches('.rc-filter-panel > summary'),
      text: control.textContent.trim()
    };
  };

  const focusControl = (root, descriptor) => {
    if (!descriptor) return;
    const candidates = [...root.querySelectorAll(CONTROL)];
    let control = descriptor.id && candidates.find(item => item.id === descriptor.id);
    if (!control && descriptor.name) {
      control = candidates.find(item => item.tagName === descriptor.tag && item.getAttribute('name') === descriptor.name &&
        (!item.matches('input[type="checkbox"],input[type="radio"]') || item.value === descriptor.value));
    }
    if (!control && descriptor.href) control = candidates.find(item => item.getAttribute('href') === descriptor.href);
    if (!control && descriptor.summary) control = root.querySelector('.rc-filter-panel > summary');
    if (!control && descriptor.more) control = root.querySelector('[data-catalog-more]');
    if (!control && descriptor.text) control = candidates.find(item => item.tagName === descriptor.tag && item.textContent.trim() === descriptor.text);
    if (control && !control.disabled && !control.closest('[hidden]')) control.focus({ preventScroll: true });
  };

  const status = (root, message) => {
    const region = root.querySelector('[data-catalog-status]');
    if (region) region.textContent = message || '';
  };

  const announceCount = (root, count) => {
    const message = (root.dataset.updated || '').replaceAll('__COUNT__', String(count));
    const label = root.querySelector('[data-catalog-count]');
    root.dataset.catalogTotal = String(count);
    if (label) {
      label.textContent = message;
      label.removeAttribute('aria-busy');
    }
    status(root, message);
  };

  const totalKey = value => {
    const url = urlFor(value);
    url.hash = '';
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sort_by');
    return `${url.pathname}${url.search}`;
  };

  const totalEndpoint = (value, resetPage = false) => {
    const url = urlFor(value);
    url.hash = '';
    if (resetPage) url.searchParams.delete('page');
    url.searchParams.set('section_id', 'rdb-catalog-total');
    return url;
  };

  const resolveTotal = async (root, destination) => {
    if (!root || !destination || !root.querySelector('[data-catalog-count]')) return;
    const key = totalKey(destination);
    if (root.dataset.catalogTotalKey === key && root.dataset.catalogTotalResolved === 'true') return;
    root.dataset.catalogTotalKey = key;
    delete root.dataset.catalogTotalResolved;
    const label = root.querySelector('[data-catalog-count]');
    label?.setAttribute('aria-busy', 'true');
    const currentRequest = ++totalRequestNumber;
    let endpoint = totalEndpoint(destination, true);
    let total = 0;
    const visited = new Set();

    try {
      while (endpoint) {
        if (visited.has(endpoint.href)) throw new Error('Catalogue total pagination loop');
        visited.add(endpoint.href);
        const response = await fetch(endpoint, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error('Catalogue total response failed');
        const html = await response.text();
        if (currentRequest !== totalRequestNumber || !root.isConnected || root.dataset.catalogTotalKey !== key) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fragment = parsed.querySelector('[data-catalog-total-fragment]');
        if (!fragment) throw new Error('Catalogue total missing');
        const pageTotal = Number(fragment.dataset.catalogTotal);
        if (!Number.isFinite(pageTotal) || pageTotal < 0) throw new Error('Catalogue total invalid');
        total += pageTotal;
        endpoint = fragment.dataset.catalogTotalNext ? totalEndpoint(fragment.dataset.catalogTotalNext) : null;
      }
      if (currentRequest !== totalRequestNumber || !root.isConnected || root.dataset.catalogTotalKey !== key) return;
      root.dataset.catalogTotalResolved = 'true';
      announceCount(root, total);
    } catch {
      if (currentRequest !== totalRequestNumber || !root.isConnected || root.dataset.catalogTotalKey !== key) return;
      const fallback = Number(root.dataset.nativeTotal);
      root.dataset.catalogTotalResolved = 'fallback';
      announceCount(root, Number.isFinite(fallback) && fallback >= 0 ? fallback : 0);
    }
  };

  const directCards = grid => [...grid.children].filter(child => child.matches('[data-catalog-card]'));

  const setupCuration = (root, destination) => {
    if (!root.matches('.rc-catalog--products-first') || root.dataset.catalogCurated) return;
    const template = root.querySelector('template[data-catalog-curation]');
    const grid = root.querySelector('[data-catalog-grid]');
    if (!template || !grid) return;
    const sort = destination.searchParams.get('sort_by');
    const mode = root.dataset.catalogCurationMode;
    if (mode === 'all' ? destination.searchParams.get('rdb_featured') !== '1' || sort && sort !== 'created-descending' :
        mode !== 'manual' || sort && sort !== 'manual') return;
    if ([...destination.searchParams.keys()].some(key => key.startsWith('filter.'))) return;
    const chosen = [];
    const known = new Set();
    directCards(template.content).forEach(card => {
      const key = card.dataset.resultKey;
      if (!key || known.has(key) || chosen.length >= 8) return;
      known.add(key);
      chosen.push(document.importNode(card, true));
    });
    if (!chosen.length) return;
    directCards(grid).forEach(card => { if (known.has(card.dataset.resultKey)) card.remove(); });
    if (Number(root.dataset.sourcePage) === 1) grid.prepend(...chosen);
    root.dataset.catalogCurated = JSON.stringify([...known]);
    if (mode === 'all') {
      const sortControl = root.querySelector('#CatalogSort');
      if (sortControl?.querySelector('option[value="manual"]')) sortControl.value = 'manual';
    }
  };

  const positionCampaign = root => {
    if (!root.matches('.rc-catalog--products-first')) return;
    const grid = root.querySelector('[data-catalog-grid]');
    const campaign = root.querySelector('[data-catalog-campaign]');
    if (!grid || !campaign) return;
    const cards = directCards(grid);
    const anchor = cards[Math.min(8, cards.length) - 1];
    if (anchor && anchor.nextElementSibling !== campaign) anchor.after(campaign);
  };

  const visiblePage = root => {
    const markers = [...root.querySelectorAll('[data-catalog-page-url]')];
    return markers.filter(card => card.getBoundingClientRect().top <= innerHeight * .35).at(-1) || markers[0];
  };

  const syncCanonical = marker => {
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && marker?.dataset.catalogCanonical) canonical.href = marker.dataset.catalogCanonical;
  };

  const sectionHash = value => SECTION_HASHES.has(value) ? value : '';
  const locationKey = (value = window.location.href) => {
    const url = new URL(value, window.location.href);
    return `${url.pathname}${url.search}`;
  };
  let currentLocationKey = locationKey();
  let currentSectionHash = sectionHash(window.location.hash);

  const pageURL = (value, hash = window.location.hash) => {
    const url = new URL(value, window.location.href);
    const preserved = sectionHash(hash);
    url.hash = preserved;
    return url.href;
  };

  const position = root => {
    const cards = [...root.querySelectorAll('[data-catalog-card]')];
    const first = visiblePage(root);
    const start = cards.indexOf(first);
    const next = cards.findIndex((item, index) => index > start && item.dataset.catalogPageUrl);
    const batch = cards.slice(Math.max(0, start), next < 0 ? cards.length : next);
    const card = batch.find(item => item.getBoundingClientRect().bottom > 140) || batch.at(-1);
    return card ? { key: card.dataset.resultKey, offset: card.getBoundingClientRect().top } : null;
  };

  const rememberPosition = root => {
    const active = visiblePage(root);
    window.history.replaceState({ ...window.history.state, rdbCatalog: position(root) }, '', pageURL(active?.dataset.catalogPageUrl || window.location.href));
    currentLocationKey = locationKey();
    syncCanonical(active);
  };

  const setupProgression = (root, destination = urlFor(window.location.href)) => {
    moreObserver?.disconnect();
    setupCuration(root, destination);
    positionCampaign(root);
    resolveTotal(root, destination);
    if (sectionHash(window.location.hash) && window.location.hash !== '#CatalogGrid') root.dataset.autoPausedForAnchor = 'true';
    const first = root.querySelector('[data-catalog-card]');
    if (!first) {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical && root.dataset.canonical) canonical.href = root.dataset.canonical;
    }
    if (first && !first.dataset.catalogPageUrl) {
      first.dataset.catalogPageUrl = destination.href;
      first.dataset.catalogCanonical = root.dataset.canonical;
      syncCanonical(first);
    }
    root.dispatchEvent(new CustomEvent('rdb:catalog-render', { bubbles: true }));
    if (root.matches('.rc-catalog--products-first')) window.rdbCurrencyPrices?.refresh();
    const more = root.querySelector('[data-catalog-more]');
    // Two automatic pages, then an explicit continuation keeps the footer reachable.
    if (!more || filterDialogs.get(root)?.dialog.open || Number(root.dataset.autoPages || 0) >= 2 || root.dataset.autoStopped || root.dataset.autoPausedForAnchor ||
        !('IntersectionObserver' in window) || navigator.connection?.saveData) return;
    moreObserver = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting) || !root.isConnected || pending || root.dataset.autoPausedForAnchor ||
          root.querySelector('[data-catalog-pagination]')?.contains(document.activeElement)) return;
      moreObserver.disconnect();
      root.dataset.autoPages = String(Number(root.dataset.autoPages || 0) + 1);
      navigate(root, urlFor(more.href), { append: true, automatic: true });
    }, { rootMargin: '500px 0px' });
    moreObserver.observe(more);
  };

  const pauseAutomaticProgression = root => {
    root.dataset.autoPausedForAnchor = 'true';
    moreObserver?.disconnect();
    if (pending && pendingAutomatic) {
      root.dataset.autoPages = String(Math.max(0, Number(root.dataset.autoPages || 0) - 1));
      pendingAutomatic = false;
      pending.abort();
    }
  };

  const updatePagePosition = () => {
    if (pending) return;
    const root = document.querySelector(ROOT);
    if (!root) return;
    const active = visiblePage(root);
    const destination = active && pageURL(active.dataset.catalogPageUrl);
    if (destination && destination !== window.location.href) {
      // Every visible batch has the same ordinary URL as its server-rendered page.
      window.history.replaceState({ ...window.history.state, rdbCatalog: position(root) }, '', destination);
      currentLocationKey = locationKey();
      syncCanonical(active);
    }
  };

  const setupDetails = root => {
    root.querySelectorAll('details').forEach(details => {
      const summary = details.querySelector(':scope > summary');
      if (summary) summary.setAttribute('aria-expanded', String(details.open));
    });
  };

  const closeFilterDialog = (root, { cancel = true, restoreFocus = true } = {}) => {
    const state = filterDialogs.get(root);
    if (!state?.open) return;
    state.open = false;
    if (cancel && state.request && pending === state.request) {
      requestNumber += 1;
      totalRequestNumber += 1;
      pending.abort();
      pending = null;
      pendingAutomatic = false;
      clearSkeletons();
      root.removeAttribute('aria-busy');
      delete root.dataset.loadMode;
      status(root, '');
    }
    state.request = null;
    if (cancel) state.draft?.forEach(({ control, value, checked, selected }) => {
      if (!control.isConnected) return;
      control.value = value;
      if (typeof checked === 'boolean') control.checked = checked;
      if (selected) [...control.options].forEach((option, index) => { option.selected = selected[index]; });
      control.dispatchEvent(new Event('input', { bubbles: true }));
    });
    if (cancel && state.form) {
      state.form.action = state.action;
      state.choices?.forEach(({ control, current }) => {
        if (current === null) control.removeAttribute('aria-current');
        else control.setAttribute('aria-current', current);
      });
    }
    state.draft = null;
    state.budget = null;
    if (state.dialog.open) state.dialog.close();
    root.removeAttribute('data-catalog-filter-open');
    state.summary.setAttribute('aria-expanded', 'false');
    document.documentElement.toggleAttribute('data-catalog-filter-lock', Boolean(document.querySelector(`${FILTER_DIALOG}[open]`)));
    if (restoreFocus && state.summary.isConnected) state.summary.focus({ preventScroll: true });
    if (cancel && root.isConnected) setupProgression(root);
  };

  const setupFilterDialog = root => {
    if (filterDialogs.has(root) ||
        typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;
    const panel = root.querySelector('.rc-filter-panel');
    const summary = panel?.querySelector(':scope > summary');
    const body = panel?.querySelector('.rc-filter-panel__body');
    if (!summary || !body) return;
    const dialog = document.createElement('dialog');
    dialog.className = 'rc-filter-dialog';
    dialog.id = `CatalogFilterDialog-${root.dataset.sectionId}`;
    dialog.setAttribute('data-catalog-filter-dialog', '');
    dialog.setAttribute('aria-labelledby', body.querySelector('h2')?.id || 'CatalogFilterTitle');
    dialog.append(body);
    root.append(dialog);
    panel.open = false;
    summary.setAttribute('aria-haspopup', 'dialog');
    summary.setAttribute('aria-controls', dialog.id);
    summary.setAttribute('aria-expanded', 'false');
    root.setAttribute('data-catalog-filter-enhanced', '');
    const state = { dialog, summary, panel, body, form: body.querySelector('[data-catalog-form]'),
      open: false, draft: null, request: null, budget: null };
    filterDialogs.set(root, state);
    dialog.addEventListener('cancel', event => {
      event.preventDefault();
      closeFilterDialog(root);
    });
    dialog.addEventListener('close', () => { if (state.open) closeFilterDialog(root); });
    dialog.addEventListener('input', event => {
      if (!event.target.matches('input[name="filter.v.price.gte"],input[name="filter.v.price.lte"]')) return;
      state.budget = null;
      dialog.querySelectorAll('[data-catalog-budget-choice]').forEach(choice => choice.removeAttribute('aria-current'));
    });
    let backdropDown = false;
    const outside = event => {
      const rect = dialog.getBoundingClientRect();
      return event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right ||
        event.clientY < rect.top || event.clientY > rect.bottom);
    };
    dialog.addEventListener('pointerdown', event => { backdropDown = outside(event); });
    dialog.addEventListener('click', event => {
      if (event.target.closest('[data-catalog-filter-close]') || (backdropDown && outside(event))) {
        event.preventDefault();
        closeFilterDialog(root);
      }
      backdropDown = false;
    });
  };

  const openFilterDialog = root => {
    const state = filterDialogs.get(root);
    if (!state || state.open) return;
    state.action = state.form?.action;
    state.choices = [...state.body.querySelectorAll('[data-catalog-budget-choice],[data-catalog-flower-choice]')]
      .map(control => ({ control, current: control.getAttribute('aria-current') }));
    state.draft = [...state.body.querySelectorAll('[data-catalog-form] input,[data-catalog-form] select,[data-catalog-form] textarea')]
      .map(control => ({ control, value: control.value, checked: control.checked,
        selected: control.options && [...control.options].map(option => option.selected) }));
    try {
      state.dialog.showModal();
      state.open = true;
      state.summary.setAttribute('aria-expanded', 'true');
      root.setAttribute('data-catalog-filter-open', '');
      document.documentElement.setAttribute('data-catalog-filter-lock', '');
      moreObserver?.disconnect();
      state.dialog.querySelector('[data-catalog-filter-close]')?.focus({ preventScroll: true });
    } catch {
      state.panel.append(state.body);
      state.dialog.remove();
      state.panel.open = true;
      state.summary.removeAttribute('aria-haspopup');
      state.summary.removeAttribute('aria-controls');
      state.summary.setAttribute('aria-expanded', 'true');
      root.removeAttribute('data-catalog-filter-enhanced');
      filterDialogs.delete(root);
    }
  };

  const stageFilterChoice = (root, link, url) => {
    const state = filterDialogs.get(root);
    if (!state?.open || !state.form || !link.closest(FILTER_DIALOG)) return false;
    let selector;
    if (link.hasAttribute('data-catalog-flower-choice')) {
      const action = new URL(state.form.action);
      action.pathname = url.pathname;
      state.form.action = action.href;
      selector = '[data-catalog-flower-choice]';
    } else if (link.hasAttribute('data-catalog-budget-choice')) {
      const prices = ['filter.v.price.gte', 'filter.v.price.lte'].map(name => {
        const input = state.form.elements.namedItem(name);
        if (!input) return null;
        const field = input.closest('[data-rdb-budget-field]');
        const base = field?.dataset.rdbBaseCurrency || 'AED';
        const currency = field?.querySelector('[data-rdb-budget-field-currency]')?.textContent.trim() || base;
        const rates = window.futureBlink_currencyRates;
        const rate = currency === base ? 1 : base === 'AED' && Number.isFinite(rates?.[base]) && rates[base] > 0 &&
          Number.isFinite(rates?.[currency]) && rates[currency] > 0 ? rates[base] / rates[currency] : null;
        const value = url.searchParams.get(name);
        if (rate === null || (value !== null && (!Number.isFinite(Number(value)) || Number(value) < 0))) return null;
        const digits = ['KWD', 'BHD', 'OMR'].includes(currency) ? 3 : 2;
        return { input, name, value, displayed: value === null ? '' : (Number(value) * rate).toFixed(digits) };
      });
      if (prices.some(price => !price)) return false;
      prices.forEach(({ input, displayed }) => {
        input.value = displayed;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
      state.budget = prices.map(({ name, value }) => ({ name, value }));
      selector = '[data-catalog-budget-choice]';
    } else return false;
    state.body.querySelectorAll(selector).forEach(choice => {
      if (choice === link) choice.setAttribute('aria-current', 'true');
      else choice.removeAttribute('aria-current');
    });
    return true;
  };

  const formURL = (form, submitter) => {
    const url = new URL(form.action || window.location.href, window.location.href);
    url.search = '';
    for (const [key, value] of new FormData(form)) {
      if (typeof value !== 'string' || key === 'page' || key === 'section_id') continue;
      if (value === '' && key.startsWith('filter.')) continue;
      url.searchParams.append(key, value);
    }
    const filterState = filterDialogs.get(form.closest(ROOT));
    if (filterState?.form === form) filterState.budget?.forEach(({ name, value }) => {
      url.searchParams.delete(name);
      if (value !== null) url.searchParams.set(name, value);
    });
    if (submitter?.name) url.searchParams.append(submitter.name, submitter.value);
    const root = form.closest(ROOT);
    if (root?.dataset.catalogCurationMode === 'all' && url.searchParams.get('sort_by') === 'manual' &&
        ![...url.searchParams.keys()].some(key => key.startsWith('filter.'))) {
      url.searchParams.set('sort_by', 'created-descending');
      url.searchParams.set('rdb_featured', '1');
    }
    return urlFor(url);
  };

  const navigate = async (root, destination, { append = false, history = true, control = null, automatic = false, restorePosition = null } = {}) => {
    const sectionId = root.dataset.sectionId;
    if (!sectionId) {
      closeFilterDialog(root, { cancel: false, restoreFocus: false });
      window.location.assign(destination.href);
      return;
    }
    if (append && pending) return;
    clearSkeletons();
    if (!append) totalRequestNumber += 1;
    if (!append && history) rememberPosition(root);
    moreObserver?.disconnect();
    pending?.abort();
    const controller = new AbortController();
    pending = controller;
    pendingAutomatic = automatic;
    const filterState = filterDialogs.get(root);
    const fromFilterDialog = !append && Boolean(control?.closest(FILTER_DIALOG));
    if (fromFilterDialog && filterState?.open) filterState.request = controller;
    const filterGroups = fromFilterDialog ? [...root.querySelectorAll('[data-catalog-filter-group]')]
      .map(group => ({ key: group.id || group.dataset.catalogFilterGroup, open: group.open })) : [];
    const currentRequest = ++requestNumber;
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    const focus = describeFocus(control, root);
    const filterWasOpen = !filterState && root.querySelector('.rc-filter-panel')?.open;
    const endpoint = new URL(destination);
    endpoint.hash = '';
    endpoint.searchParams.set('section_id', sectionId);
    root.setAttribute('aria-busy', 'true');
    root.dataset.loadMode = append ? 'append' : 'replace';
    const moreLink = root.querySelector('[data-catalog-more]');
    const note = root.querySelector('[data-catalog-loading]');
    if (append && moreLink) moreLink.setAttribute('aria-disabled', 'true');
    if (note) {
      note.hidden = true;
      delete note.dataset.error;
      note.querySelector('[data-loading-copy]').textContent = root.dataset.loading;
    }
    const feedbackTimer = setTimeout(() => { if (append && note) note.hidden = false; }, 180);
    status(root, root.dataset.loading);
    if (!append && root.closest('main')?.dataset.template?.startsWith('collection')) showSkeletons(root);

    try {
      const response = await fetch(endpoint, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Section response failed');
      const html = await response.text();
      if (currentRequest !== requestNumber) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = [...parsed.querySelectorAll(ROOT)].find(item => item.dataset.sectionId === sectionId);
      if (!incoming || !root.isConnected) throw new Error('Catalogue section missing');
      clearSkeletons();

      const active = document.activeElement;
      const restore = root.contains(active) ? describeFocus(active, root) : active === document.body ? focus : null;
      if (automatic && root.dataset.autoPausedForAnchor) {
        root.removeAttribute('aria-busy');
        delete root.dataset.loadMode;
        moreLink?.removeAttribute('aria-disabled');
        if (note) note.hidden = true;
        status(root, '');
      } else if (append) {
        const grid = root.querySelector('[data-catalog-grid]');
        const nextGrid = incoming.querySelector('[data-catalog-grid]');
        const pagination = root.querySelector('[data-catalog-pagination]');
        const nextPagination = incoming.querySelector('[data-catalog-pagination]');
        if (!grid || !nextGrid || !pagination || !nextPagination) throw new Error('Catalogue results missing');
        if (Number(incoming.dataset.sourcePage) <= Number(root.dataset.sourcePage)) throw new Error('Catalogue page did not advance');
        const known = new Set([...directCards(grid).map(card => card.dataset.resultKey),
          ...JSON.parse(root.dataset.catalogCurated || '[]')]);
        const additions = directCards(nextGrid).filter(card => {
          const key = card.dataset.resultKey;
          if (!key || known.has(key)) return false;
          known.add(key);
          return true;
        });
        if (!additions.length && directCards(nextGrid).length) {
          const curated = new Set(JSON.parse(root.dataset.catalogCurated || '[]'));
          if (!directCards(nextGrid).every(card => curated.has(card.dataset.resultKey))) throw new Error('Catalogue page did not advance');
        }
        const fragment = document.createDocumentFragment();
        additions.forEach((card, index) => {
          const copy = document.importNode(card, true);
          copy.classList.add('rc-card-arrived');
          copy.addEventListener('animationend', () => copy.classList.remove('rc-card-arrived'), { once: true });
          if (index === 0) {
            copy.dataset.catalogPageUrl = destination.href;
            copy.dataset.catalogCanonical = incoming.dataset.canonical;
          }
          fragment.append(copy);
        });
        grid.append(fragment);
        positionCampaign(root);
        root.dataset.sourcePage = incoming.dataset.sourcePage;
        pagination.replaceWith(document.importNode(nextPagination, true));
        root.querySelector('[data-catalog-empty]')?.remove();
        const empty = incoming.querySelector('[data-catalog-empty]');
        if (!directCards(grid).length && empty) grid.after(document.importNode(empty, true));
        root.removeAttribute('aria-busy');
        delete root.dataset.loadMode;
        status(root, '');
        // Keep keyboard users at their position even when the final more link disappears.
        if (!automatic && restore?.more && additions.length) {
          const firstNew = directCards(grid).find(card => card.dataset.resultKey === additions[0].dataset.resultKey);
          firstNew?.querySelector('h3 a,a')?.focus({ preventScroll: true });
        } else if (!automatic && restore?.more && !root.querySelector('[data-catalog-more]')) {
          directCards(grid).at(-1)?.querySelector('h3 a,a')?.focus({ preventScroll: true });
        } else if (!automatic) focusControl(root, restore);
      } else {
        const replacement = document.importNode(incoming, true);
        const filter = replacement.querySelector('.rc-filter-panel');
        if (filter && filterWasOpen) filter.open = true;
        replacement.querySelectorAll('[data-catalog-filter-group]').forEach((group, index) => {
          const previous = filterGroups.find(item => item.key && item.key === (group.id || group.dataset.catalogFilterGroup)) || filterGroups[index];
          if (previous) group.open = previous.open;
        });
        replacement.removeAttribute('aria-busy');
        setupDetails(replacement);
        closeFilterDialog(root, { cancel: false, restoreFocus: false });
        root.replaceWith(replacement);
        setupFilterDialog(replacement);
        if (history && destination.href !== window.location.href) {
          window.history.pushState({ ...window.history.state, rdbCatalog: null }, '', destination);
          currentLocationKey = locationKey();
          currentSectionHash = sectionHash(window.location.hash);
        }
        setupProgression(replacement, destination);
        if (fromFilterDialog) replacement.querySelector('.rc-filter-panel > summary')?.focus({ preventScroll: true });
        else focusControl(replacement, restore);
        const anchor = restorePosition && [...replacement.querySelectorAll('[data-catalog-card]')].find(card => card.dataset.resultKey === restorePosition.key);
        if (anchor) window.scrollBy({ top: anchor.getBoundingClientRect().top - restorePosition.offset, behavior: 'instant' });
        else if (fromFilterDialog) (replacement.querySelector('.rc-controls') || replacement.querySelector('[data-catalog-grid]'))?.scrollIntoView({ behavior: 'instant', block: 'start' });
        else if (control?.closest('[data-catalog-pagination]')) replacement.querySelector('[data-catalog-grid]')?.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    } catch (error) {
      if (currentRequest !== requestNumber) return;
      clearSkeletons();
      root.removeAttribute('aria-busy');
      delete root.dataset.loadMode;
      if (automatic && root.dataset.autoPausedForAnchor) {
        moreLink?.removeAttribute('aria-disabled');
        if (note) note.hidden = true;
        status(root, '');
      } else if (append) {
        root.dataset.autoStopped = 'true';
        moreLink?.removeAttribute('aria-disabled');
        if (note) {
          note.hidden = false;
          note.dataset.error = 'true';
          note.querySelector('[data-loading-copy]').textContent = root.dataset.loadError;
        }
        status(root, root.dataset.loadError);
      } else {
        closeFilterDialog(root, { cancel: false, restoreFocus: false });
        window.location.assign(destination.href);
      }
    } finally {
      window.clearTimeout(timeout);
      window.clearTimeout(feedbackTimer);
      if (currentRequest === requestNumber) {
        pending = null;
        pendingAutomatic = false;
        const currentRoot = document.querySelector(ROOT);
        if (currentRoot) setupProgression(currentRoot);
      }
    }
  };

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const summary = event.target.closest('.rc-filter-panel > summary');
    const filterRoot = summary?.closest(ROOT);
    if (filterRoot && filterDialogs.has(filterRoot)) {
      event.preventDefault();
      openFilterDialog(filterRoot);
      return;
    }
    const link = event.target.closest('a[data-catalog-link],a[data-catalog-more]');
    const root = link?.closest(ROOT);
    if (!root || link.hasAttribute('download') || (link.target && link.target !== '_self') || !link.hasAttribute('href')) return;
    if (link.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
    const url = urlFor(link.href);
    if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return;
    event.preventDefault();
    if (stageFilterChoice(root, link, url)) return;
    navigate(root, url, { append: link.hasAttribute('data-catalog-more'), control: link });
  });

  document.addEventListener('submit', event => {
    const form = event.target;
    if (event.defaultPrevented || !form.matches('[data-catalog-form]') || form.method.toLowerCase() !== 'get') return;
    const root = form.closest(ROOT);
    if (!root || (form.target && form.target !== '_self')) return;
    const url = formURL(form, event.submitter);
    if (url.origin !== window.location.origin) return;
    event.preventDefault();
    const control = root.contains(document.activeElement) ? document.activeElement : event.submitter;
    navigate(root, url, { control });
  });

  document.addEventListener('change', event => {
    const control = event.target;
    if (!control.matches('[data-catalog-autosubmit]')) return;
    const form = control.form;
    if (form?.matches('[data-catalog-form]') && form.closest(ROOT)) form.requestSubmit();
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    if (event.target.closest(`${FILTER_DIALOG}[open]`)) return;
    const details = event.target.closest('details[open]');
    if (!details?.closest(ROOT)) return;
    details.open = false;
    const summary = details.querySelector(':scope > summary');
    summary?.setAttribute('aria-expanded', 'false');
    summary?.focus({ preventScroll: true });
    event.preventDefault();
  });

  document.addEventListener('toggle', event => {
    const details = event.target;
    if (!details.matches('details') || !details.closest(ROOT)) return;
    if (details.matches('.rc-filter-panel') && filterDialogs.has(details.closest(ROOT))) return;
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(details.open));
  }, true);

  window.addEventListener('popstate', event => {
    if (window.__rdbCollectionCampaignPanels?.ownsHistoryEvent?.(event)) return;
    const root = document.querySelector(ROOT);
    const nextLocationKey = locationKey();
    const nextSectionHash = sectionHash(window.location.hash);
    const ownsFragmentTransition = nextLocationKey === currentLocationKey && Boolean(currentSectionHash || nextSectionHash);
    currentLocationKey = nextLocationKey;
    currentSectionHash = nextSectionHash;
    if (ownsFragmentTransition) return;
    if (root) navigate(root, urlFor(window.location.href), { history: false, restorePosition: event.state?.rdbCatalog });
  });

  window.addEventListener('hashchange', () => {
    currentLocationKey = locationKey();
    currentSectionHash = sectionHash(window.location.hash);
    const root = document.querySelector(ROOT);
    if (!root) return;
    if (currentSectionHash && currentSectionHash !== '#CatalogGrid') {
      pauseAutomaticProgression(root);
    } else if (root.dataset.autoPausedForAnchor) {
      delete root.dataset.autoPausedForAnchor;
      setupProgression(root);
    }
  });

  window.addEventListener('scroll', () => {
    clearTimeout(positionTimer);
    positionTimer = setTimeout(updatePagePosition, 120);
  }, { passive: true });

  document.addEventListener('click', event => {
    const root = event.target.closest(ROOT);
    const link = root && event.target.closest('a:not([data-catalog-link]):not([data-catalog-more])');
    if (!link) return;
    const hash = sectionHash(new URL(link.href, window.location.href).hash);
    const shortcut = link.matches('.rc-shop-shortcut') && hash && event.button === 0 &&
      !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey &&
      (!link.target || link.target === '_self');
    if (shortcut && hash !== '#CatalogGrid') {
      pauseAutomaticProgression(root);
    } else if (shortcut) {
      delete root.dataset.autoPausedForAnchor;
      window.requestAnimationFrame(() => { if (root.isConnected) setupProgression(root); });
    }
    rememberPosition(root);
  });

  window.addEventListener('pagehide', () => {
    clearSkeletons();
    requestNumber += 1;
    pending?.abort();
    pendingAutomatic = false;
    moreObserver?.disconnect();
    document.querySelectorAll(ROOT).forEach(root => {
      closeFilterDialog(root, { restoreFocus: false });
      root.removeAttribute('aria-busy');
    });
  });

  const initialize = root => { setupDetails(root); setupFilterDialog(root); setupProgression(root); };
  document.querySelectorAll(ROOT).forEach(initialize);
  window.addEventListener('pageshow', event => {
    if (event.persisted) { pending = null; document.querySelectorAll(ROOT).forEach(initialize); }
  });
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll(ROOT).forEach(initialize));
  document.addEventListener('shopify:section:unload', event => {
    if (!event.target.querySelector(ROOT)) return;
    event.target.querySelectorAll(ROOT).forEach(root => closeFilterDialog(root, { restoreFocus: false }));
    requestNumber += 1;
    pending?.abort();
    pending = null;
    pendingAutomatic = false;
    moreObserver?.disconnect();
    clearSkeletons();
  });
})();
